/* eslint-disable max-lines */
import { mapGetters, mapMutations } from "vuex";
import OrderApi from "../../services/api/orderApi.js";
import { PromoCodeEnum } from "../../services/PromoCodeTypes.js";
import paymentTerms from "./paymentTerms/paymentTerms.vue";

import freeShippingPromoCode from "./freeShippingPromoCode/freeShippingPromoCode.vue";
import PromoCode from "./promoCode/promoCode";

const OrderApiService = new OrderApi();

export default {
  components: {
    paymentTerms,
    freeShippingPromoCode,
    PromoCode,
  },
  data() {
    return {
      isPromoCodeExpanded: false,
      checkoutSteps: [
        {
          name: "checkout",
          label: "Your Cart",
        },
        {
          name: "checkoutShipping",
          label: "Shipping & Billing",
        },
        {
          name: "checkoutOrderReview",
          label: "Order Review",
        },
        {
          name: "checkoutAddCardInfo",
          label: "Payment",
        },
      ],
      promo_code: "",
      selectedRetailer: null,
      retailers: [],
      isAdmin: false,
      cart: {
        sub_carts: [],
        total: null,
        promo_code: null,
        valid_mov: false,
      },
      products: [],
      checkout_in_progress: false,
      quantityRequestProductIDs: [],
    };
  },

  methods: {
    getRetailers,
    updateQuantity,
    removeProduct,
    applyPromoCode,
    removePromoCode,
    checkout,
    selectRetailerCallback,
    removeRetailer,
    getCart,
    validate,
    sendError,
    initialPageLoad,
    showSumDiscount,
    getDiscountAmount,
    ...mapMutations(["clearPromoCodeState"]),
  },

  beforeMount() {
    if (!this.user) {
      this.$router.push("/home");
    }

    if (this.user && this.user.role === "ADMIN") {
      this.getRetailers();
      this.isAdmin = true;
    }

    this.getCart().then(this.initialPageLoad);
  },

  mounted() {
    window.addEventListener("focus", this.getCart);
    this.$eventBus.$on("cart-updated", this.getCart);

    this.clearPromoCodeState();
  },

  beforeDestroy() {
    window.removeEventListener("focus", this.getCart);
    this.$eventBus.$off("cart-updated", this.getCart);
  },

  computed: {
    cartPromoCode() {
      return this.cart.promo_code;
    },
    isPromoCodeLoading() {
      return this.$store.getters.checkoutState.isPromoCodeLoading;
    },
    orderHasAdminPromoCode() {
      return this.cart.sub_carts.filter((subCart) => {
        return subCart.admin_promo_code;
      });
    },
    ...mapGetters({
      promoCodeState: "promoCodeState",
      user: "userState",
    }),
  },

  watch: {
    cartPromoCode(val) {
      this.promo_code = val || "";
    },
  },
};

async function getRetailers() {
  try {
    const { data } = await this.$utils.getAllRetailers();

    this.retailers = data.retailers_dropdown;
  } catch (error) {
    this.$swal("Attention", this.$utils.formatErrorMessage("retailers"));
  }
}

function selectRetailerCallback(val) {
  this.selectedRetailer = val;
}

function removeRetailer() {
  this.selectedRetailer = null;
}

function initialPageLoad() {
  this.$ma.trackEvent({
    action: "View Checkout Page",
    properties: {
      "Product Quantity": this.cart.sub_carts.reduce(
        (accumulator, subCart) => accumulator + subCart.cart_items.length,
        0
      ),
      "Brands in Order": this.cart.sub_carts.map((cart) => cart.brand_info.brand_name),
      "Total Value": this.cart.total,
      "Product Ids": this.cart.sub_carts.flatMap((sub) => sub.cart_items.map((item) => item.product_id)),
      "Product Names": this.cart.sub_carts.flatMap((sub) => sub.cart_items.map((item) => item.product_name)),
      "Order ID": this.cart.sub_carts.number,
    },
  });
}

async function updateQuantity(quantity, subCart, product) {
  if (quantity <= 0) {
    this.removeProduct(product, subCart);
  } else {
    const variantId = this.cart.sub_carts[subCart].cart_items[product].variant_id;

    this.quantityRequestProductIDs = [...this.quantityRequestProductIDs, variantId];
    try {
      this.cart = (
        await this.$utils.updateCart({
          variant_id: variantId,
          total_quantity: quantity,
        })
      ).data;
      await this.$utils.checkCartChanges(this, this.cart);
    } catch (error) {
      this.getCart();
      this.$swal("Attention", error.response.data.error_message);
    } finally {
      this.quantityRequestProductIDs = this.quantityRequestProductIDs.filter(
        (productIdItem) => productIdItem !== variantId
      );
    }
  }
}

async function removeProduct(product, subCart) {
  this.checkout_in_progress = true;
  try {
    this.cart = (
      await this.$utils.removeProductFromCart(this.cart.sub_carts[subCart].cart_items[product].variant_id)
    ).data;
    await this.$utils.checkCartChanges(this, this.cart);
    await this.$store.commit("cart/decrement");
  } catch (error) {
    this.getCart();
    this.$swal("Attention", error.response.data.error_message);
  }
  this.checkout_in_progress = false;
}

async function getCart() {
  try {
    this.cart = (await this.$utils.getCart()).data;
    this.$utils.checkCartChanges(this, this.cart);
  } catch (error) {
    this.$swal("Attention", error.response.data.error_message);
  }

  if (this.cart.promo_code) {
    this.promo_code = this.cart.promo_code;
  }
}

// eslint-disable-next-line complexity
function validate(subCart) {
  const _self = this;

  if ((_self.isAdmin && !_self.selectedRetailer) || _self.checkout_in_progress) {
    return;
  }

  if ((subCart && !subCart.valid_mov) || (!subCart && !this.cart.valid_mov)) {
    const message = subCart
      ? `Minimum order value for ${subCart.brand_info.brand_name} not reached. ($${subCart.subtotal.toFixed(
          this.$utils.constants.PRICE_FORMAT_DECIMAL_PLACES
        )} of $${subCart.brand_info.minimum_order_value.toFixed(this.$utils.constants.PRICE_FORMAT_DECIMAL_PLACES)})`
      : this.$utils.constants.MINIMUM_ORDER_VALUE_ERROR_MESSAGE;

    this.$swal("", message);
    return;
  }

  _self.checkout(subCart);
}

async function checkout(subCart) {
  const _self = this;

  _self.checkout_in_progress = true;

  if (_self.isAdmin) {
    const orderItems = [];

    this.cart.sub_carts.forEach((subCart) => {
      subCart.cart_items.forEach((item) => {
        orderItems.push({
          variant_id: item.variant_id,
          quantity: item.quantity,
        });
      });
    });

    // eslint-disable-next-line one-var
    const createCustomAssortmentRequest = {
      custom_assortment_items: orderItems,
      retailer_id: _self.selectedRetailer.id,
    };

    OrderApiService.createCustomAssortment(createCustomAssortmentRequest)
      .then(() => {
        _self.checkout_in_progress = false;
        _self
          .$swal(
            "Success",
            "You have successuflly created a custom assortment order for retailer " + _self.selectedRetailer.name + "."
          )
          .then(function () {
            _self.$router.push("/home");
          });
      })
      .catch((error) => {
        _self.checkout_in_progress = false;
        if (error.response) {
          _self.$swal("Attention", error.response.data.error_message);
        } else {
          _self.$logger.notify(new Error(error));
        }
      });
  } else {
    if (subCart) {
      await OrderApiService.checkoutBrand(subCart.brand_info.id);
    } else {
      await OrderApiService.checkoutBrand(null);
    }

    _self.checkout_in_progress = false;
    _self.$router.push("/checkout/shipping");
  }
}

function applyPromoCode() {
  this.$store.commit("setPromoCodeInvalid", false);
  this.$store.commit("setPromoCodeErrorMessage", null);

  if (this.promo_code.length && this.$root.alphaNumericPattern.test(this.promo_code.toLowerCase())) {
    this.$store.commit("setPromoCodeLoadingState", true);
    OrderApiService.applyPromoCode({
      promo_code: this.promo_code,
    })
      .then((response) => {

        const {data} = response;
        const {type, promo_code_creator, amount} = data;

        this.$ma.trackEvent({
          action: "Promo Code",
          properties: {
            Success: true,
            "Promo code": this.promo_code,
            "Promo Type": type,
            "Promo Creator": promo_code_creator,
            "Promo Value": amount,
          },
        });
        if (type === PromoCodeEnum.FREE_SHIPPING.value) {
          this.$swal("Success", "You have successfully applied a promo code!");
        } else {
          this.$swal("Success", "You have successfully applied a promo code! " + response.data.message);
        }
        this.getCart();
      })
      .catch((error) => {
        if (error.response.data.status === this.$utils.constants.HTTP_STATUS.CONFLICT) {
          this.$ma.trackEvent({
            action: "Promo Code",
            properties: {
              Success: false,
              Error: "Promo code already used",
              "Promo code": this.promo_code,
            },
          });
          this.sendError("Promo code already used (via API)", this.promo_code);
        } else {
          this.$ma.trackEvent({
            action: "Promo Code",
            properties: {
              Success: false,
              Error: "Promo Code not found",
              "Promo code": this.promo_code,
            },
          });
          this.sendError("Promo code not found (via API)", this.promo_code);
        }

        this.$store.commit("setPromoCodeInvalid", true);

        if (error.response.data.error_message) {
          this.$store.commit("setPromoCodeErrorMessage", error.response.data.error_message);
        }
      })
      .finally(() => {
        this.promo_code = "";
        this.$store.commit("setPromoCodeLoadingState", false);
      });
  } else {
    this.$ma.trackEvent({
      action: "Promo Code",
      properties: {
        Success: false,
        Error: "Promo Code not valid",
        "Promo code": this.promo_code,
      },
    });
    this.sendError("Promo code not valid (via Frontend)", this.promo_code);
    this.$swal("Attention", "Promo code not valid.");
  }
}

function removePromoCode(code) {
  this.$store.commit("setPromoCodeLoadingState", true);
  OrderApiService.removePromoCode(code)
    .then(() => {
      this.getCart();
      this.$swal("Success", "The promo code for your order has been successfully removed.");
    })
    .catch((error) => {
      if (error.response) {
        this.$swal("Attention", error.response.data.error_message);
      } else {
        this.$logger.notify(new Error(error));
      }
    })
    .finally(() => {
      this.$store.commit("setPromoCodeLoadingState", false);
    });
}

function sendError(text, code) {
  const user = this.$session.getUserInfo();

  this.$logger.notify(new Error(text + ". Promo code: [" + code + "] User: " + user.email));
}

function showSumDiscount() {
  if (
    this.orderHasAdminPromoCode?.length &&
    this.orderHasAdminPromoCode?.find((item) => item.promo_code_type === "AMOUNT" && item.promo_code)
  ) {
    return true;
  }

  return this.cart.sub_carts?.find((item) => item.promo_code_type === "AMOUNT" && item.promo_code);
}

function getDiscountAmount() {
  if (this.orderHasAdminPromoCode?.length) {
    return this.orderHasAdminPromoCode[0].promo_code_amount;
  }

  return this.cart.sub_carts
    .reduce((total, item) => {
      if (item.promo_code_type === "AMOUNT") {
        return total + item.promo_code_amount;
      }
      return total;
    }, 0)
    ?.toFixed(2);
}
